import { Box } from "@mui/material";
import { Alert, Button, Typography } from "@platform/design-system";
import {
  CensusForm,
  LOADING,
  SubmitButton,
  getFormInitState,
} from "@platform/census-components";
import { useOutletContext, useParams } from "react-router";

import { CircularProgressCentered } from "../../../components/CircularProgressCentered";
import { ProcessedCensus } from "../../../components/processedCensus";
import { useCensusDocument } from "../../../hooks/useCensusDocument";
import { WorkbenchContext } from "../types";

function WorkbenchCensus(): React.JSX.Element {
  const { id } = useParams();
  const { state } = useOutletContext<WorkbenchContext>();

  const rfpAdditionalInfo = (
    state.data?.rfp?.details as Record<string, unknown> | undefined
  )?.additionalInfo as
    | {
        benAdminMetadata?: { censusAvailable?: boolean | string | null };
        requestedSystemMetadata?: { censusAvailable?: boolean | string | null };
      }
    | undefined;

  const censusAvailable =
    rfpAdditionalInfo?.benAdminMetadata?.censusAvailable ??
    rfpAdditionalInfo?.requestedSystemMetadata?.censusAvailable;

  const isCensusFromEnExpected =
    censusAvailable === true || censusAvailable === "yes";

  const {
    isLoading,
    isError,
    isCensusExists,
    isDeletingCensusDocument,
    onDeleteCensusDocument,
    formState,
    onChange,
    onConfirm,
    updateFormState,
    updateFormStateValue,
    isInvalid,
  } = useCensusDocument(id);

  const resetForm = (): void => {
    updateFormState(getFormInitState());
  };

  const handleDeleteCensusDocument = async (): Promise<void> => {
    await onDeleteCensusDocument();
    resetForm();
  };

  if (isLoading) {
    return <CircularProgressCentered />;
  }

  if (isError) {
    return (
      <Typography textAlign="center" color="danger">
        <strong>
          Error getting census information. Please try again later!
        </strong>
      </Typography>
    );
  }

  const enCensusBanner = isCensusFromEnExpected ? (
    <Alert severity="info" sx={{ mb: 0 }}>
      Census will be provided from Employee Navigator within 24h of RFP
      submission. Please check the folder &quot;EN Census&quot; in SharePoint
      under &quot;Documents from Broker&quot;.
    </Alert>
  ) : null;

  if (isCensusExists) {
    return (
      <>
        {enCensusBanner}
        <ProcessedCensus
          rfpId={id}
          onDelete={handleDeleteCensusDocument}
          isDeleting={isDeletingCensusDocument}
        />
      </>
    );
  }

  return (
    <>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Box flex={1}>{enCensusBanner}</Box>
        <Button
          disabled={formState.state === LOADING}
          onClick={resetForm}
          variant="tertiary"
        >
          Reset Form
        </Button>
      </Box>
      <CensusForm
        formState={formState}
        onChange={onChange}
        updateFormStateValue={updateFormStateValue}
      />
      <Box textAlign="end" mt={3}>
        <SubmitButton
          formState={formState}
          handleConfirm={onConfirm}
          updateFormState={updateFormState}
          isInvalid={isInvalid}
        />
      </Box>
    </>
  );
}

export default WorkbenchCensus;
